<script setup>
import { ref, onBeforeUnmount, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/api'
import Swal from 'sweetalert2'
import { encodeId } from '@/utils/obfuscate'
import { isAdmin } from '@/composables/useAuth'

const isAdminUser = computed(() => isAdmin())

const router = useRouter()

// Roles reales desde backend (solo ADMIN)
const roles = ref([])
const cargandoRoles = ref(false)
const errorRoles = ref('')

async function cargarRoles() {
  if (!isAdminUser.value) return
  cargandoRoles.value = true
  errorRoles.value = ''
  try {
    const { data } = await api.get('/roles')
    // Contrato GET /roles: res.json(roles) -> array [{id,nombre}]
    const lista = Array.isArray(data) ? data : (data.roles || data.data || [])
    roles.value = lista
  } catch (err) {
    errorRoles.value = err.response?.data?.message || err.message || 'No se pudieron cargar roles'
    Swal.fire('Error', errorRoles.value, 'error')
  } finally {
    cargandoRoles.value = false
  }
}

onMounted(() => {
  if (isAdminUser.value) cargarRoles()
})
const GENEROS = [
    { value: 'M', label: 'Masculino' },
    { value: 'F', label: 'Femenino' },
    { value: 'Otro', label: 'Otro' }
]

// formulario
const form = ref({
    nombre: '',
    identificacion: '',
    genero: '',
    email: '',
    password: '',
    telefono: '',
    direccion: '',
    fecha_nacimiento: '',
    rol_id: '', // ADMIN elige; EMPLEADO no envía (backend resuelve SOCIO por nombre)
    estado: 1  // activo
})

const loading = ref(false)
const errors = ref({})

// manejo de archivo de imagen
const avatarFile = ref(null)
const preview = ref(null)

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp']
const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2 MB

function validateImageFile(file) {
    if (!ALLOWED_MIME.includes(file.type)) {
        Swal.fire('Archivo no válido', 'Formato permitido: JPG, PNG o WEBP.', 'warning')
        return false
    }
    if (file.size > MAX_FILE_SIZE) {
        Swal.fire('Archivo muy grande', 'El tamaño máximo es 2 MB.', 'warning')
        return false
    }
    return true
}

function revokePreview() {
    if (preview.value && preview.value.startsWith('blob:')) {
        URL.revokeObjectURL(preview.value)
    }
}

function onFile(e) {
    const f = e.target.files?.[0]
    if (f && !validateImageFile(f)) {
        e.target.value = ''
        avatarFile.value = null
        revokePreview()
        preview.value = null
        return
    }
    revokePreview()
    avatarFile.value = f || null
    preview.value = f ? URL.createObjectURL(f) : null
}

onBeforeUnmount(() => {
    revokePreview()
})

function validate() {
    errors.value = {}
    if (!form.value.nombre?.trim()) errors.value.nombre = 'Requerido'
    if (!form.value.identificacion?.trim()) errors.value.identificacion = 'Requerido'
    if (isAdminUser.value && !form.value.rol_id) errors.value.rol_id = 'Seleccione un rol'
    if (form.value.email && form.value.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email.trim())) {
        errors.value.email = 'Email no válido'
    }
    return Object.keys(errors.value).length === 0
}

async function save({ goToMembership } = { goToMembership: false }) {
    if (!validate()) return
    try {
        loading.value = true

        // Construir payload sin hardcodear rol_id para EMPLEADO; backend resuelve SOCIO por nombre
        const payload = {
            nombre: form.value.nombre,
            identificacion: form.value.identificacion,
            genero: form.value.genero,
            email: form.value.email,
            password: form.value.password,
            telefono: form.value.telefono,
            direccion: form.value.direccion,
            fecha_nacimiento: form.value.fecha_nacimiento,
            estado: form.value.estado,
        }
        if (isAdminUser.value && form.value.rol_id) {
            payload.rol_id = form.value.rol_id
        }
        const fd = new FormData()
        Object.entries(payload).forEach(([key, val]) => {
            if (val !== '' && val !== null && val !== undefined) {
                fd.append(key, val)
            }
        })
        if (avatarFile.value) fd.append('avatar', avatarFile.value)

        const { data } = await api.post('/users', fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })

        Swal.fire({
            icon: 'success',
            title: 'Usuario creado',
            text: `${data.nombre} fue registrado correctamente.`,
            timer: 1400,
            showConfirmButton: false
        })

        if (goToMembership) {
            router.push({ name: 'SocioDetalle', params: { id: encodeId(data.id) }, query: { nueva_membresia: 1 } })
        } else {
            router.push({ name: 'Socios' })
        }
    } catch (err) {
        const msg = err.response?.data?.message || err.message || 'Error al crear usuario'
        Swal.fire('Error', msg, 'error')
    } finally {
        loading.value = false
    }
}

function cancel() {
    router.back()
}
</script>

<template>
    <div class="container-fluid py-4">
        <div class="card p-4">
            <h5 class="mb-3">Nuevo Usuario</h5>

            <div class="row g-3">

                <!-- Nombre -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Nombre *</label>
                    <input v-model="form.nombre" type="text" class="form-control"
                        :class="{ 'is-invalid': errors.nombre }" />
                    <div class="invalid-feedback" v-if="errors.nombre">{{ errors.nombre }}</div>
                </div>

                <!-- Identificación -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Identificación *</label>
                    <input v-model="form.identificacion" type="text" class="form-control"
                        :class="{ 'is-invalid': errors.identificacion }" />
                    <div class="invalid-feedback" v-if="errors.identificacion">{{ errors.identificacion }}</div>
                </div>

                <!-- Género -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Género</label>
                    <select v-model="form.genero" class="form-select">
                        <option value="">— Seleccione —</option>
                        <option v-for="g in GENEROS" :key="g.value" :value="g.value">{{ g.label }}</option>
                    </select>
                </div>

                <!-- Rol -->
                <div v-if="isAdminUser" class="col-12 col-md-6">
                    <label class="form-label">Rol *</label>
                    <select v-model="form.rol_id" class="form-select" :class="{ 'is-invalid': errors.rol_id }" :disabled="cargandoRoles">
                        <option value="">— Seleccione —</option>
                        <option v-for="r in roles" :key="r.id" :value="r.id">{{ r.nombre }}</option>
                    </select>
                    <div class="invalid-feedback" v-if="errors.rol_id">{{ errors.rol_id }}</div>
                    <small v-if="cargandoRoles" class="text-muted">Cargando roles...</small>
                    <small v-if="errorRoles" class="text-danger">{{ errorRoles }}</small>
                </div>
                <div v-else class="col-12 col-md-6">
                    <label class="form-label">Rol *</label>
                    <input type="text" class="form-control bg-light" value="SOCIO" readonly />
                    <small class="text-muted">Tu rol solo permite crear usuarios SOCIO. Se asignará automáticamente.</small>
                </div>

                <!-- Email -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Email</label>
                    <input v-model="form.email" type="email" class="form-control" :class="{ 'is-invalid': errors.email }" />
                    <div class="invalid-feedback" v-if="errors.email">{{ errors.email }}</div>
                </div>

                <!-- Contraseña -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Contraseña</label>
                    <input v-model="form.password" type="password" class="form-control" autocomplete="new-password" />
                </div>

                <!-- Teléfono -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Teléfono</label>
                    <input v-model="form.telefono" type="text" class="form-control" />
                </div>

                <!-- Dirección -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Dirección</label>
                    <input v-model="form.direccion" type="text" class="form-control" />
                </div>

                <!-- Fecha nacimiento -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Fecha de nacimiento</label>
                    <input v-model="form.fecha_nacimiento" type="date" class="form-control" />
                </div>

                <!-- Estado -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Estado</label>
                    <select v-model.number="form.estado" class="form-select">
                        <option :value="1">Activo</option>
                        <option :value="0">Inactivo</option>
                    </select>
                </div>

                <!-- Avatar -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Foto de perfil</label>
                    <input type="file" class="form-control" accept="image/jpeg,image/png,image/webp" @change="onFile" />
                    <small class="text-muted">JPG, PNG, WEBP máx. 2 MB.</small>
                    <div v-if="preview" class="mt-2 text-center">
                        <img :src="preview" alt="preview" style="max-width: 150px; border-radius: 12px;" />
                    </div>
                </div>
            </div>

            <!-- acciones -->
            <div class="d-flex flex-wrap gap-2 justify-content-end mt-4">
                <button class="btn btn-light" :disabled="loading" @click="cancel">Cancelar</button>
                <button class="btn btn-secondary" :disabled="loading" @click="save({ goToMembership: false })">
                    Guardar
                </button>
                <button class="btn btn-primary" :disabled="loading" @click="save({ goToMembership: true })">
                    Guardar y asignar membresía
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.card {
    max-width: 1100px;
    margin-inline: auto;
}
</style>
